import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import Copy01Icon from "@redotech/redo-icons/copy-01.svg";
import { toast } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import classNames from "classnames";
import { memo, useCallback, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import type { PartnerDetail } from "@redotech/admin-sdk/rpc/schema/partners/shared";

import { RedoAdminRpcClientContext } from "../app/redo-admin-rpc-client-provider";
import { DetailPageBackButton } from "../onboardings/components/DetailPageBackButton";
// Reuse the merchant detail page's layout CSS so the partner detail
// surface reads as the same kind of page (sticky white header with
// title + tags row, in-header tab strip, centered 1000px content
// column). Partners only have one level of navigation, so the tab
// strip lives in the white header — not a grey bar below.
import * as shellStyles from "../onboardings/merchants/merchant-detail-page.module.css";
import { ImpersonatePartnerButton } from "./components/impersonate-partner-button";
import { PartnerActionPanel } from "./components/partner-action-panel";
import { PartnerAuthEventsList } from "./components/partner-auth-events-list";
import { PartnerStatusBadge } from "./components/partner-status-badge";
import { mapPartnerErrorToFieldError } from "./lib/map-partner-error-to-field-errors";

type TabKey = "overview" | "auth-events" | "installs" | "payouts";
const VALID_TABS: TabKey[] = ["overview", "auth-events", "installs", "payouts"];

export const PartnerDetailPage = memo(function PartnerDetailPage() {
  const rpcClient = useRequiredContext(RedoAdminRpcClientContext);
  const navigate = useNavigate();
  const { partnerId } = useParams<{ partnerId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const tab: TabKey = parseTab(searchParams.get("tab"));
  const setTab = useCallback(
    (next: TabKey) => {
      const params = new URLSearchParams(searchParams);
      if (next === "overview") {
        params.delete("tab");
      } else {
        params.set("tab", next);
      }
      setSearchParams(params, { replace: false });
    },
    [searchParams, setSearchParams],
  );

  const [refreshKey, setRefreshKey] = useState(0);
  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  const detailLoad = useLoad(
    async (signal) => {
      if (!partnerId) return null;
      return rpcClient.getPartner({ partnerId }, { signal });
    },
    [rpcClient, partnerId, refreshKey],
  );

  const handleBack = useCallback(() => navigate("/partners"), [navigate]);

  if (detailLoad.pending && detailLoad.value == null) {
    return (
      <div className={shellStyles.pageWrapper}>
        <div className={shellStyles.loadingContainer}>Loading partner…</div>
      </div>
    );
  }
  if (detailLoad.error) {
    return (
      <div className={shellStyles.pageWrapper}>
        <div className={shellStyles.errorContainer}>
          <Text fontSize="md" textColor="error">
            Failed to load partner.
          </Text>
          <RedoButton
            hierarchy="secondary"
            onClick={refresh}
            size="sm"
            text="Retry"
          />
        </div>
      </div>
    );
  }
  const partner = detailLoad.value?.partner;
  if (!partner) {
    return (
      <div className={shellStyles.pageWrapper}>
        <div className={shellStyles.errorContainer}>Partner not found.</div>
      </div>
    );
  }

  return (
    <div className={shellStyles.pageWrapper}>
      <div className={shellStyles.topHeader}>
        <DetailPageBackButton onClick={handleBack} />
        <div className={shellStyles.topHeaderContent}>
          <div className={shellStyles.topHeaderMain}>
            <div className={shellStyles.topHeaderLeft}>
              <h1 className={shellStyles.pageTitle}>{partner.name}</h1>
              <div className={shellStyles.headerTags}>
                <PartnerStatusBadge status={partner.status} />
              </div>
            </div>
            <div className={shellStyles.headerActions}>
              <ImpersonatePartnerButton partner={partner} />
              <PartnerActionPanel
                onPartnerChanged={refresh}
                partner={partner}
              />
            </div>
          </div>
          <div className={shellStyles.productNavBar}>
            {VALID_TABS.map((t) => (
              <button
                className={classNames(
                  shellStyles.productTab,
                  tab === t && shellStyles.productTabActive,
                )}
                key={t}
                onClick={() => setTab(t)}
                type="button"
              >
                {TAB_LABELS[t]}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className={shellStyles.pageContainer}>
        {tab === "overview" && (
          <OverviewTab onPartnerChanged={refresh} partner={partner} />
        )}
        {tab === "auth-events" && (
          <PartnerAuthEventsList events={partner.recentAuthEvents} />
        )}
        {tab === "installs" && (
          <DeferredTab description="Install attribution lands in PR07." />
        )}
        {tab === "payouts" && (
          <DeferredTab description="Stripe payouts land in PR10." />
        )}
      </div>
    </div>
  );
});

const TAB_LABELS: Record<TabKey, string> = {
  overview: "Overview",
  "auth-events": "Auth events",
  installs: "Installs",
  payouts: "Payouts",
};

function parseTab(raw: string | null): TabKey {
  if (raw && (VALID_TABS as string[]).includes(raw)) {
    return raw as TabKey;
  }
  return "overview";
}

interface EditableNameRowProps {
  partner: PartnerDetail;
  onUpdated: () => void;
}

const EditableNameRow = memo(function EditableNameRow({
  partner,
  onUpdated,
}: EditableNameRowProps) {
  const rpcClient = useRequiredContext(RedoAdminRpcClientContext);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(partner.name);
  const [submitting, setSubmitting] = useState(false);

  const cancel = useCallback(() => {
    setDraft(partner.name);
    setEditing(false);
  }, [partner.name]);

  const save = useCallback(async () => {
    const trimmed = draft.trim();
    if (trimmed.length === 0) {
      toast("Name cannot be blank.", { variant: "error" });
      return;
    }
    if (trimmed === partner.name) {
      setEditing(false);
      return;
    }
    setSubmitting(true);
    try {
      await rpcClient.updatePartner({ partnerId: partner.id, name: trimmed });
      toast("Name updated.", { variant: "success" });
      setEditing(false);
      onUpdated();
    } catch (err) {
      const mapping = mapPartnerErrorToFieldError(err);
      toast(
        mapping?.message ??
          (err instanceof Error ? err.message : "Failed to update name."),
        { variant: "error" },
      );
    } finally {
      setSubmitting(false);
    }
  }, [draft, partner.id, partner.name, rpcClient, onUpdated]);

  return (
    <Flex align="center" dir="row" gap="md">
      <Text fontSize="sm" fontWeight="semibold">
        Name
      </Text>
      {editing ? (
        <>
          <RedoTextInput
            placeholder="Partner name"
            setValue={setDraft}
            value={draft}
          />
          <RedoButton
            disabled={submitting}
            hierarchy="primary"
            onClick={() => void save()}
            size="xs"
            text={submitting ? "Saving…" : "Save"}
          />
          <RedoButton
            disabled={submitting}
            hierarchy="tertiary"
            onClick={cancel}
            size="xs"
            text="Cancel"
          />
        </>
      ) : (
        <>
          <Text fontSize="sm">{partner.name}</Text>
          <RedoButton
            hierarchy="tertiary"
            onClick={() => setEditing(true)}
            size="xs"
            text="Edit"
          />
        </>
      )}
    </Flex>
  );
});

interface OverviewTabProps {
  partner: PartnerDetail;
  onPartnerChanged: () => void;
}

const OverviewTab = memo(function OverviewTab({
  partner,
  onPartnerChanged,
}: OverviewTabProps) {
  const referralLink = `https://partners.getredo.com/r/${partner.referralCode}`;
  const copyReferralLink = useCallback(() => {
    void navigator.clipboard.writeText(referralLink).then(() => {
      toast("Referral link copied.", { variant: "success" });
    });
  }, [referralLink]);

  return (
    <div className={shellStyles.tabContent}>
      <div className={shellStyles.metaSection}>
        <EditableNameRow onUpdated={onPartnerChanged} partner={partner} />
        <MetadataRow label="Partner ID" value={partner.id} />
        <MetadataRow label="Email" value={partner.email} />
        <MetadataRow
          label="Partner type"
          value={partnerTypeLabel(partner.partnerType)}
        />
        <MetadataRow label="Referral code" value={partner.referralCode} />
        <Flex align="center" dir="row" gap="md">
          <Text fontSize="sm" fontWeight="semibold">
            Referral link
          </Text>
          <Text fontSize="sm">{referralLink}</Text>
          <RedoButton
            hierarchy="tertiary"
            IconLeading={Copy01Icon}
            onClick={copyReferralLink}
            size="xs"
            text="Copy"
          />
        </Flex>
        <MetadataRow label="Created by" value={partner.createdBy} />
        <MetadataRow
          label="Created"
          value={new Date(partner.createdAt).toLocaleString()}
        />
        <MetadataRow
          label="Last updated"
          value={new Date(partner.updatedAt).toLocaleString()}
        />
      </div>
    </div>
  );
});

function MetadataRow({ label, value }: { label: string; value: string }) {
  return (
    <Flex align="center" dir="row" gap="md">
      <Text fontSize="sm" fontWeight="semibold">
        {label}
      </Text>
      <Text fontSize="sm">{value}</Text>
    </Flex>
  );
}

function partnerTypeLabel(type: string): string {
  switch (type) {
    case "individual":
      return "Individual";
    case "app":
      return "App";
    case "agency":
      return "Agency";
    default:
      return type;
  }
}

function DeferredTab({ description }: { description: string }) {
  return (
    <Flex align="center" justify="center" p="3xl">
      <Text fontSize="md" textColor="tertiary">
        {description}
      </Text>
    </Flex>
  );
}
